import React from 'react';

import { Card, CardContent, CardMedia, Grid, makeStyles, Typography, useMediaQuery, useTheme, Divider } from '@material-ui/core';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/Phone';

import { FuneralHome } from '../../../static/models/FuneralHome';
import { SearchLocation } from '../../../static/models/SearchLocation';
import Settings from '../../../static/models/Settings';
import { calculateHaversineDistance } from '../../../toolboxes/reusable-logic/location-functions';
import { REFERRAL_ACCOUNT_IDENTIFIER_PARAM_KEY } from '../../page-components/provider-search-page/ProviderSearchPage';

const smallFontSize = '0.875rem';

const useStyles = makeStyles(theme => ({
    card: {
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
        padding: '0',
        marginBottom: theme.spacing(1),
    },
    cardBody: {
        display: 'flex',
        paddingBottom: 0,
    },
    cardDetails: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    distance: {
        textAlign: 'right',
        fontSize: smallFontSize,
    },
    providerName: {
        marginBottom: '0.375rem',
        fontWeight: 500,
    },
    addressBlock: {
        marginBottom: '2rem',
        fontSize: smallFontSize,
    },
    contactBlock: {},
    contactItem: {
        marginRight: '2rem',
    },
    cardMedia: {
        width: '100%',
        display: "flex",
        [theme.breakpoints.down('sm')]: {
            alignSelf: 'center',
        },
    },
    cardMediaImage: {
        maxHeight: 186,
        objectFit: 'contain',
        [theme.breakpoints.down('sm')]: {
            maxHeight: 197,
        },
    },
    price: {
        fontWeight: 'bold',
        marginLeft: 'auto',
        [theme.breakpoints.down('sm')]: {
            marginLeft: 'inherit',
            marginTop: '1rem',
            marginBottom: '2rem',
        },
    },
    cardActions: {
        padding: '0 1rem',
    },
    actionButtons: {
        alignItems: 'left',
    },
    selectPackageButton: {
        width: 200,
    },
    iconLink: {
        display: 'flex',
        alignItems: 'center',
        color: '#000',
        textDecoration: 'none',
    },
    icon: {
        marginRight: '0.25rem',
    },
    planNowBlock: {
        textAlign: 'right',
    },
    planNow: {
        textTransform: 'uppercase',
        color: '#B28623',
        textDecoration: 'none',
        fontSize: '0.875rem',
        border: '1px solid #B28623',
        borderRadius: '0.875rem',
        padding: '0.15rem 0.6rem',
        lineHeight: '1rem;',
    },
    divider: {
        width: '5px',
        background: theme.palette.primary.main
    },
}));

function formattedDistance(location: FuneralHome, searchLocation: SearchLocation | null) {
    const distance = calculateHaversineDistance(
        location?.latitude,
        location?.longitude,
        searchLocation?.latitude,
        searchLocation?.longitude,
    ).toFixed(0);
    return `${distance} MI AWAY`;
}

const ProviderListItem: React.FunctionComponent<PropShape> = ({
    location,
    searchLocation,
    settings,
    referralAccountIdentifier,
    selectedLocation,
    setSelectedLocation
}) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    let planNowUrl = `${settings.storefrontProtocol}${location.subdomain}.${settings.storefrontBaseDomain}`;
    if (referralAccountIdentifier) {
        planNowUrl += `?${REFERRAL_ACCOUNT_IDENTIFIER_PARAM_KEY}=${referralAccountIdentifier}`;
    }

    const logoUrl = location.contractLogoUrl ? location.contractLogoUrl : `https://media.efuneral.com/account-logos/${location.subdomain}.png?version=1.1`;

    return (
        <Grid container>
            <Grid item xs={1}>
                {selectedLocation === location && <Divider id={location.id + "-" + location.name + "-divider-bar"} orientation="vertical" className={classes.divider} />}
            </Grid>
            <Grid item xs={11}>
                <Card id={location.id + "-" + location.name + "-account-card"} className={classes.card} elevation={0} onClick={() => setSelectedLocation(location)} style={{ cursor: 'pointer' }}>
                    <CardContent className={classes.cardBody}>
                        <Grid container direction={isMobile ? 'column' : 'row'} spacing={2}>
                            <Grid item sm={isMobile ? undefined : 4} className={classes.cardMedia}>
                                <CardMedia
                                    image={logoUrl}
                                    alt={`image of ${location.name}`}
                                    className={classes.cardMediaImage}
                                    component="img"
                                />
                            </Grid>
                            <Grid item container className={classes.cardDetails}>
                                <Grid item className={classes.distance}>
                                    {formattedDistance(location, searchLocation)}
                                </Grid>
                                <Typography variant="body1" className={classes.providerName}>
                                    {location.name}
                                </Typography>
                                <Grid item className={classes.addressBlock}>
                                    <Typography variant="body2">{location.address}</Typography>
                                    <Typography variant="body2">
                                        {location.city}, {location.state} {location.zip}
                                    </Typography>
                                </Grid>
                                <Grid container className={classes.contactBlock}>
                                    <Grid item className={classes.contactItem}>
                                        <a href={`tel:${location.phone}`} className={classes.iconLink}>
                                            <PhoneIcon className={classes.icon} color="primary" fontSize="inherit" /> Call Us
                                        </a>
                                    </Grid>
                                    <Grid item>
                                        <a href={`mailto:${location.notificationEmail}`} className={classes.iconLink}>
                                            <EmailIcon className={classes.icon} color="primary" fontSize="inherit" /> Email Us
                                        </a>
                                    </Grid>
                                </Grid>
                                <Grid className={classes.planNowBlock}>
                                    <a className={classes.planNow} href={planNowUrl}>
                                        Plan Now
                                    </a>
                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};

interface PropShape {
    location: FuneralHome;
    searchLocation: SearchLocation | null;
    settings: Settings;
    referralAccountIdentifier: string | null;
    selectedLocation: FuneralHome | undefined;
    setSelectedLocation: (location: FuneralHome | undefined) => void;
}

ProviderListItem.defaultProps = {
    // Set default values for props
    searchLocation: null,
    referralAccountIdentifier: null,
};

export default ProviderListItem;
